import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { PM_REL_STATUS } from '@/lib/constants';

export default function PMSlideOver({ open, onClose, pm, onSaved }) {
    const { profile } = useAuth();
    const isEdit = !!pm?.id;
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (!open) return; setForm(pm ? { ...pm } : { company_name: '', relationship_status: 'Active' }); }, [open, pm?.id]);
    if (!open) return null;
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const submit = async (e) => {
        e.preventDefault(); setSaving(true);
        try {
            const payload = { ...form };
            for (const k of Object.keys(payload)) if (payload[k] === '') payload[k] = null;
            payload.service_fee_pct = payload.service_fee_pct ? Number(payload.service_fee_pct) : null;
            if (isEdit) {
                const { error } = await supabase.from('property_managers').update(payload).eq('id', pm.id);
                if (error) throw error;
            } else {
                payload.organization_id = profile.organization_id; payload.created_by = profile.id;
                const { error } = await supabase.from('property_managers').insert(payload);
                if (error) throw error;
            }
            toast.success(isEdit ? 'Property Manager updated' : 'Property Manager added');
            onSaved?.(); onClose();
        } catch (err) { toast.error(err.message); } finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-[60] flex justify-end" onClick={onClose}>
            <div className="absolute inset-0 bg-black/40" />
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="relative bg-white w-full max-w-lg h-full overflow-y-auto shadow-2xl" data-testid="pm-slideover">
                <div className="sticky top-0 bg-white border-b border-gray-100 p-5 flex items-center justify-between z-10">
                    <h2 className="text-xl font-bold">{isEdit ? 'Edit Property Manager' : 'Add Property Manager'}</h2>
                    <button type="button" onClick={onClose}><X className="h-5 w-5" /></button>
                </div>
                <div className="p-5 space-y-3">
                    <F label="Company name *"><input required value={form.company_name||''} onChange={set('company_name')} className="input" data-testid="pm-company" /></F>
                    <div className="grid grid-cols-2 gap-3">
                        <F label="Contact person"><input value={form.contact_person||''} onChange={set('contact_person')} className="input" /></F>
                        <F label="Phone"><input value={form.phone||''} onChange={set('phone')} className="input" /></F>
                        <F label="WhatsApp"><input value={form.whatsapp||''} onChange={set('whatsapp')} className="input" /></F>
                        <F label="Email"><input type="email" value={form.email||''} onChange={set('email')} className="input" /></F>
                    </div>
                    <F label="Managed areas"><textarea rows={2} value={form.managed_areas||''} onChange={set('managed_areas')} className="input" placeholder="e.g. Dubai Marina, JLT, Business Bay" /></F>
                    <div className="grid grid-cols-2 gap-3">
                        <F label="Service fee %"><input type="number" step="0.1" value={form.service_fee_pct||''} onChange={set('service_fee_pct')} className="input" /></F>
                        <F label="Status"><select value={form.relationship_status||'Active'} onChange={set('relationship_status')} className="input">{PM_REL_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}</select></F>
                        <F label="Contract start"><input type="date" value={form.contract_start_date||''} onChange={set('contract_start_date')} className="input" /></F>
                        <F label="Contract end"><input type="date" value={form.contract_end_date||''} onChange={set('contract_end_date')} className="input" /></F>
                    </div>
                    <F label="Notes"><textarea rows={3} value={form.notes||''} onChange={set('notes')} className="input" /></F>
                </div>
                <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4 flex gap-3">
                    <button type="button" onClick={onClose} className="flex-1 h-11 rounded-md border border-gray-300 font-medium">Cancel</button>
                    <button type="submit" disabled={saving} className="flex-1 h-11 rounded-md bg-aks-navy text-white font-semibold disabled:opacity-60" data-testid="pm-submit">{saving ? 'Saving…' : 'Save'}</button>
                </div>
                <style>{`.input{margin-top:.25rem;width:100%;height:2.5rem;padding:0 .75rem;border:1px solid #d1d5db;border-radius:.375rem;outline:none;font-size:14px;background:#fff}.input:focus{border-color:#C9A84C;box-shadow:0 0 0 3px rgba(201,168,76,.25)}textarea.input{height:auto;padding:.5rem .75rem}`}</style>
            </form>
        </div>
    );
}
function F({ label, children }) { return <label className="block"><span className="text-[12px] font-medium text-aks-text">{label}</span>{children}</label>; }
